import { createPackagePaymentSessionFn } from "@/actions/payment/handler";
import { authClient } from "@/auth/auth-client";
import {
  readCheckoutMarketingFields,
  trackCheckoutStarted,
} from "@/components/analytics/conversions";
import { IyzicoCheckoutDialog } from "@/components/payment/IyzicoCheckoutDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { createWhatsAppUrl } from "@/lib/whatsapp";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Check, Clock, Lock, MessageCircle, ShieldCheck } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

interface CoachingPurchasePreviewDialogProps {
  packageId?: string;
  packageTitle: string;
  durationMonths?: number;
  price: number;
  oldPrice?: number;
  features?: string[];
  children: ReactNode;
}

export function CoachingPurchasePreviewDialog({
  packageId,
  packageTitle,
  durationMonths,
  price,
  oldPrice,
  features = [],
  children,
}: CoachingPurchasePreviewDialogProps) {
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [fallbackToSupport, setFallbackToSupport] = useState(false);

  const priceFormatted = `₺${price.toLocaleString("tr-TR")}`;
  const oldPriceFormatted =
    oldPrice && oldPrice > price
      ? `₺${oldPrice.toLocaleString("tr-TR")}`
      : null;

  const checkoutMutation = useMutation({
    mutationFn: createPackagePaymentSessionFn,
    onSuccess: (res) => {
      if (
        res.status === 200 &&
        res.body &&
        "paymentPageUrl" in res.body &&
        res.body.paymentPageUrl
      ) {
        if (res.body.paymentId && res.body.amount && packageId)
          trackCheckoutStarted(
            res.body.paymentId,
            "package",
            packageId,
            res.body.amount,
          );
        setCheckoutUrl(res.body.paymentPageUrl);
        setIsOpen(false);
        setIsCheckoutOpen(true);
      } else {
        toast.error("Online ödeme sayfası açılamadı. Lütfen tekrar deneyin.");
        setFallbackToSupport(true);
      }
    },
    onError: (_err) => {
      toast.error(
        "Ödeme altyapısına bağlanırken bir sorun oluştu. Lütfen tekrar deneyin.",
      );
      setFallbackToSupport(true);
    },
  });

  const handleStartPurchase = () => {
    if (!session?.user) {
      toast.info("Paketi satın almak için lütfen giriş yapın.");
      void navigate({
        to: "/password/login",
        search: { redirect: "/coachs" },
      });
      return;
    }

    if (!packageId) {
      setFallbackToSupport(true);
      toast.info("Satın alma işlemini WhatsApp üzerinden tamamlayabilirsiniz.");
      return;
    }

    checkoutMutation.mutate({
      data: {
        packageId,
        durationMonths,
        ...readCheckoutMarketingFields(),
      },
    });
  };

  const whatsappMessage = `Merhaba, "${packageTitle}" ${durationMonths ? `(${durationMonths} Aylık)` : ""} paketi hakkında bilgi almak ve satın alma işlemini tamamlamak istiyorum.`;
  const whatsappUrl = createWhatsAppUrl(whatsappMessage);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="max-h-[95vh] w-[95vw] max-w-lg overflow-y-auto rounded-xl border border-border bg-card p-6 shadow-xl sm:w-full">
          <DialogHeader className="space-y-2 text-left">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="font-semibold text-foreground/80 text-xs"
              >
                Koçluk Hizmeti
              </Badge>
              {durationMonths && (
                <span className="flex items-center gap-1 font-medium text-muted-foreground text-xs">
                  <Clock className="h-3.5 w-3.5" /> {durationMonths} Aylık
                </span>
              )}
            </div>
            <DialogTitle className="font-bold text-2xl text-foreground tracking-tight">
              {packageTitle}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Satın alma öncesi paket ve teslimat detayları
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Price box */}
            <div className="rounded-lg border border-border bg-muted/40 p-4 sm:p-5">
              <p className="font-semibold text-muted-foreground text-xs uppercase tracking-wider">
                Toplam Tutar (KDV Dâhil)
              </p>
              <div className="mt-1.5 flex items-baseline gap-2.5">
                <span className="font-extrabold text-3xl text-primary tracking-tight sm:text-4xl">
                  {priceFormatted}
                </span>
                {oldPriceFormatted && (
                  <span className="font-semibold text-base text-muted-foreground line-through">
                    {oldPriceFormatted}
                  </span>
                )}
                {durationMonths && durationMonths > 1 && (
                  <span className="text-muted-foreground text-xs">
                    (₺
                    {Math.round(price / durationMonths).toLocaleString("tr-TR")}
                    /ay)
                  </span>
                )}
              </div>
            </div>

            {/* Feature preview */}
            {features.length > 0 && (
              <div className="space-y-2">
                <p className="font-semibold text-muted-foreground text-xs uppercase tracking-wider">
                  Paket Kapsamı
                </p>
                <ul className="space-y-2 text-sm">
                  {features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span className="text-foreground/90">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Trust & Guarantee points */}
            <div className="space-y-3 border-border border-t pt-4 text-muted-foreground text-xs leading-relaxed">
              <div className="flex items-start gap-2.5">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <strong className="text-foreground">
                    İyzico Güvenli Ödeme:{" "}
                  </strong>
                  256-bit SSL ve 3D Secure ile korunur. Kredi kartı bilgileriniz
                  sistemde saklanmaz.
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <strong className="text-foreground">
                    Hızlı Koç İletişimi:{" "}
                  </strong>
                  Satın alma sonrası 24 saat içinde koçunuz sizinle iletişime
                  geçer ve süreç başlatılır.
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <strong className="text-foreground">Yasal Güvence: </strong>
                  Mesafeli Satış Sözleşmesi ve Ön Bilgilendirme Formu onayınızla
                  güvenli alışveriş.
                </div>
              </div>
            </div>

            {fallbackToSupport && (
              <div className="space-y-1 rounded-lg border border-destructive/20 bg-destructive/5 p-3.5 text-foreground text-xs">
                <p className="font-semibold text-destructive">
                  Ödeme altyapısına bağlanırken bir aksaklık oluştu.
                </p>
                <p className="text-muted-foreground">
                  Lütfen butona tekrar basarak yeniden deneyin veya doğrudan
                  WhatsApp üzerinden iletişime geçin.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex flex-col gap-2 pt-2 sm:flex-col">
            {!session?.user ? (
              <Button
                onClick={handleStartPurchase}
                className="h-11 w-full rounded-lg bg-primary font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                Giriş Yaparak Satın Al
              </Button>
            ) : (
              <Button
                onClick={handleStartPurchase}
                disabled={checkoutMutation.isPending}
                className="h-11 w-full rounded-lg bg-primary font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                {checkoutMutation.isPending
                  ? "Ödeme Formu Hazırlanıyor..."
                  : "Güvenli Ödemeye Geç"}
              </Button>
            )}

            {fallbackToSupport && (
              <Button
                asChild
                variant="outline"
                className="h-10 w-full text-muted-foreground text-xs hover:text-foreground"
              >
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                  WhatsApp ile Satın Al / Havale-EFT ile Tamamla
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embedded Iyzico Checkout Modal when URL is returned */}
      <IyzicoCheckoutDialog
        open={isCheckoutOpen}
        onOpenChange={setIsCheckoutOpen}
        paymentPageUrl={checkoutUrl}
        title={`${packageTitle} Satın Al`}
      />
    </>
  );
}
